import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import NavbarIconsGenericComponent from '../generic-nav-bar/NavbarIconsGenericComponent';
import {
  startLoadingStatisticsSpecialist,
  startLoadingPlatformHealth,
  startLoadingFlowEngineStatistics
} from '../../app/actions/statisticActions';

const formatMs = (ms) => {
  if (!ms) return '—';
  const minutes = Math.round(Number(ms) / 60000);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return `${hours} h ${rest} min`;
};

const CountBarList = ({ rows, labelKey, empty }) => {
  const max = Math.max(0, ...(rows || []).map((row) => Number(row.total) || 0));
  if (!rows?.length) {
    return <p className="text-muted small mb-0">{empty}</p>;
  }
  return (
    <div className="mb-3">
      {rows.map((row) => {
        const pct = max ? Math.round((Number(row.total) / max) * 100) : 0;
        return (
          <div className="mb-2" key={row[labelKey] || row.process || row.type}>
            <div className="d-flex justify-content-between small">
              <span>{row[labelKey] || row.process}</span>
              <span>{row.total}</span>
            </div>
            <div className="progress" style={{ height: 8 }}>
              <div className="progress-bar" role="progressbar" style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const TimeBarList = ({ rows, labelKey, empty }) => {
  const max = Math.max(0, ...(rows || []).map((row) => Number(row.medianMs) || 0));
  if (!rows?.length) {
    return <p className="text-muted small mb-0">{empty}</p>;
  }
  return (
    <div className="mb-3">
      {rows.map((row) => {
        const pct = max ? Math.round((Number(row.medianMs) / max) * 100) : 0;
        return (
          <div className="mb-2" key={row[labelKey] || row.process || row.user || row.role}>
            <div className="d-flex justify-content-between small">
              <span>{row[labelKey] || row.process}</span>
              <span>mediana {formatMs(row.medianMs)} · p90 {formatMs(row.p90Ms)}</span>
            </div>
            <div className="progress" style={{ height: 8 }}>
              <div className="progress-bar" role="progressbar" style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const SimpleTable = ({ columns, rows, empty }) => {
  if (!rows?.length) {
    return <p className="text-muted small mb-0">{empty}</p>;
  }
  return (
    <div className="table-responsive">
      <table className="table table-sm table-striped">
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key}>{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row.process || row.reason || index}>
              {columns.map((col) => (
                <td key={col.key}>{col.render ? col.render(row) : row[col.key]}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ListStatisticsSpecialistOps = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { specialistStatisticList, platformHealth, flowEngineReports } = useSelector((state) => state.statistic);

  useEffect(() => {
    dispatch(startLoadingStatisticsSpecialist());
    dispatch(startLoadingPlatformHealth());
    dispatch(startLoadingFlowEngineStatistics());
  }, [dispatch]);

  const extra = (Array.isArray(specialistStatisticList) ? specialistStatisticList : []).filter((row) =>
    ['suggestCase', 'supportRequest', 'userInterest'].includes(row.class)
  );

  const reports = flowEngineReports || {};
  const queueTrayTotals = ['Gestor', 'Validador', 'Editor', 'Pendiente', 'Espera'].map((type) => ({
    type,
    total: (reports.queue || []).reduce((sum, row) => sum + (Number(row[type]) || 0), 0)
  }));
  const queueByModule = (reports.queue || []).map((row) => ({
    process: row.process,
    total: ['Gestor', 'Validador', 'Editor'].reduce((sum, key) => sum + (Number(row[key]) || 0), 0)
  }));

  return (
    <>
      <NavbarIconsGenericComponent componentName="specialistStatisticList" />
      <div className="container mt-4 pb-5">
        <h2>{t('semanticSearch.stats.specialistOpsTitle', { defaultValue: 'Especialista — flujos y sugerencias' })}</h2>
        <p>
          <Link to="/specialist-flow-cmi">
            {t('semanticSearch.stats.openCmi', { defaultValue: 'Abrir panel de control de casos' })}
          </Link>
        </p>

        <h4 className="mt-4">Cola por bandeja y módulo</h4>
        <p className="small text-muted">Carga actual. Publicado no cuenta como bandeja operativa.</p>
        <div className="row">
          <div className="col-md-6">
            <p className="small fw-semibold mb-1">Totales por bandeja</p>
            <CountBarList rows={queueTrayTotals} labelKey="type" empty="Sin datos de cola." />
          </div>
          <div className="col-md-6">
            <p className="small fw-semibold mb-1">En bandeja operativa por módulo</p>
            <CountBarList rows={queueByModule} labelKey="process" empty="Sin datos de cola." />
          </div>
        </div>
        <SimpleTable
          empty="Sin datos de cola."
          columns={[
            { key: 'process', label: 'Módulo' },
            { key: 'Gestor', label: 'Gestor' },
            { key: 'Validador', label: 'Validador' },
            { key: 'Editor', label: 'Editor' },
            { key: 'Pendiente', label: 'Pendiente' },
            { key: 'Espera', label: 'Espera licencia/experto' },
            { key: 'Publicado', label: 'Publicado' }
          ]}
          rows={reports.queue}
        />

        <h4 className="mt-4">Tiempo entre eventos (mediana / p90)</h4>
        <p className="small text-muted">Intervalos entre filas consecutivas de historial del mismo caso. Sirve para ver cuellos de botella.</p>
        <SimpleTable
          empty="Sin historial suficiente."
          columns={[
            { key: 'process', label: 'Módulo' },
            { key: 'medianMs', label: 'Mediana', render: (row) => formatMs(row.medianMs) },
            { key: 'p90Ms', label: 'p90', render: (row) => formatMs(row.p90Ms) },
            { key: 'totalMs', label: 'Total', render: (row) => formatMs(row.totalMs) },
            { key: 'samples', label: 'Muestras' }
          ]}
          rows={reports.leadTime}
        />
        <TimeBarList rows={reports.leadTime} labelKey="process" empty="Sin historial suficiente." />

        <h4 className="mt-4">Tiempo de ciclo (primer evento → último)</h4>
        <SimpleTable
          empty="Sin ciclos suficientes."
          columns={[
            { key: 'process', label: 'Módulo' },
            { key: 'medianMs', label: 'Mediana', render: (row) => formatMs(row.medianMs) },
            { key: 'p90Ms', label: 'p90', render: (row) => formatMs(row.p90Ms) },
            { key: 'samples', label: 'Casos' }
          ]}
          rows={reports.cycleTime}
        />

        <h4 className="mt-4">Tiempos por rol / bandeja</h4>
        <div className="row">
          <div className="col-md-6">
            <TimeBarList rows={reports.leadTimeByRole} labelKey="role" empty="Sin tiempos por rol." />
          </div>
          <div className="col-md-6">
            <TimeBarList rows={reports.leadTimeByTray} labelKey="tray" empty="Sin tiempos por bandeja." />
          </div>
        </div>

        <h4 className="mt-4">Tiempos por usuario (top 20, mediana)</h4>
        <p className="small text-muted">Nombre operativo. No se muestra correo.</p>
        <SimpleTable
          empty="Sin tiempos por usuario."
          columns={[
            { key: 'user', label: 'Usuario' },
            { key: 'medianMs', label: 'Mediana', render: (row) => formatMs(row.medianMs) },
            { key: 'p90Ms', label: 'p90', render: (row) => formatMs(row.p90Ms) },
            { key: 'totalMs', label: 'Total', render: (row) => formatMs(row.totalMs) },
            { key: 'samples', label: 'Tramos' }
          ]}
          rows={reports.leadTimeByUser}
        />
        <TimeBarList rows={reports.leadTimeByUser} labelKey="user" empty="Sin tiempos por usuario." />

        <h4 className="mt-4">Publicados últimos 7 días</h4>
        <CountBarList rows={reports.publishedWeek} labelKey="process" empty="Sin publicaciones en la semana." />
        <SimpleTable
          empty="Sin publicaciones en la semana."
          columns={[
            { key: 'process', label: 'Módulo' },
            { key: 'total', label: 'Publicados' }
          ]}
          rows={reports.publishedWeek}
        />

        <h4 className="mt-4">Devoluciones (retrabajo)</h4>
        <CountBarList rows={reports.returns} labelKey="process" empty="Sin devoluciones registradas." />
        <SimpleTable
          empty="Sin devoluciones registradas."
          columns={[
            { key: 'process', label: 'Módulo' },
            { key: 'total', label: 'Eventos devolver' }
          ]}
          rows={reports.returns}
        />

        <h4 className="mt-4">Rechazos con motivo (sin dueño)</h4>
        <SimpleTable
          empty="Sin rechazos con motivo."
          columns={[
            { key: 'process', label: 'Módulo' },
            { key: 'reason', label: 'Motivo' },
            { key: 'total', label: 'Casos' }
          ]}
          rows={reports.rejections}
        />

        <h4 className="mt-4">Envejecimiento (sin transición &gt; 7 días)</h4>
        <SimpleTable
          empty="Ningún caso envejecido."
          columns={[
            { key: 'process', label: 'Módulo' },
            { key: 'total', label: 'Casos en bandeja' },
            { key: 'days', label: 'Umbral (días)' }
          ]}
          rows={reports.aging}
        />

        <h4 className="mt-4">Esperas actuales</h4>
        <SimpleTable
          empty="Sin esperas."
          columns={[
            { key: 'process', label: 'Módulo' },
            { key: 'license', label: 'Licencia' },
            { key: 'expert', label: 'Experto' }
          ]}
          rows={reports.waits}
        />

        <h4 className="mt-4">Conteos de operación</h4>
        {extra.map((row) => (
          <div className="row" key={row.class}>
            <div className="col-md-7">{t(`reporter-statistic-specialist.large.${row.class}`, { defaultValue: row.class })}</div>
            <div className="col-md-3">{row.value}</div>
          </div>
        ))}
        <h4 className="mt-4">Publicados (inventario)</h4>
        <ul>
          {(platformHealth?.published || []).map((row) => (
            <li key={row.module_key}>{row.module_key}: {row.published} / {row.total}</li>
          ))}
        </ul>
        <h4>Embeddings</h4>
        <ul>
          {(platformHealth?.embeddings || []).map((row) => (
            <li key={row.module_key}>{row.module_key}: {row.count}</li>
          ))}
        </ul>
      </div>
    </>
  );
};

export default ListStatisticsSpecialistOps;
